/**
 * A QR encoder, because the alternative was a dependency.
 *
 * The upload link is the one thing on this dialog that has to leave the
 * screen and land on a phone, and a QR code is how it does that. `qrcode` is
 * not in `package.json`, and adding one while five other agents edit this
 * repo means a lockfile conflict for every one of them — so the encoder lives
 * here. It is the small half of the format: byte mode, error correction level
 * M, versions 1 to 10 (up to 213 bytes, where an upload URL is around 100).
 * Nothing else in the product needs a QR code; when something does, this
 * moves into `components/fx` rather than growing options.
 *
 * Level M rather than L on purpose: the code is printed, taped to a folder of
 * paper forms and photographed under office light. M recovers 15 % damage for
 * about 25 % more modules, which costs nothing on a 150px tile.
 *
 * Verified against CoreImage's `CIQRCodeGenerator` (see `qrEncode.test.ts`) —
 * an independent implementation, matrix for matrix, mask included.
 */

/** Data codewords per block, error-correction codewords per block, for the
 *  level-M versions this encoder covers. `[ecPerBlock, blocksA, dataA,
 *  blocksB, dataB]`; group B is the one block size larger, where a version
 *  has two groups. */
const BLOCKS_M: Record<number, [number, number, number, number, number]> = {
  1: [10, 1, 16, 0, 0],
  2: [16, 1, 28, 0, 0],
  3: [26, 1, 44, 0, 0],
  4: [18, 2, 32, 0, 0],
  5: [24, 2, 43, 0, 0],
  6: [16, 4, 27, 0, 0],
  7: [18, 4, 31, 0, 0],
  8: [22, 2, 38, 2, 39],
  9: [22, 3, 36, 2, 37],
  10: [26, 4, 43, 1, 44],
}

/** Row/column centres of the alignment patterns, per version. */
const ALIGNMENT: Record<number, number[]> = {
  1: [],
  2: [6, 18],
  3: [6, 22],
  4: [6, 26],
  5: [6, 30],
  6: [6, 34],
  7: [6, 22, 38],
  8: [6, 24, 42],
  9: [6, 26, 46],
  10: [6, 28, 50],
}

/** Unused bits after the interleaved codewords, per version. */
function remainderBits(version: number): number {
  return version >= 2 && version <= 6 ? 7 : 0
}

function dataCodewords(version: number): number {
  const [, blocksA, dataA, blocksB, dataB] = BLOCKS_M[version]
  return blocksA * dataA + blocksB * dataB
}

// -- GF(256) ---------------------------------------------------------------

const EXP = new Uint8Array(512)
const LOG = new Uint8Array(256)
{
  let x = 1
  for (let i = 0; i < 255; i += 1) {
    EXP[i] = x
    LOG[x] = i
    x <<= 1
    if (x & 0x100) x ^= 0x11d // the QR field's primitive polynomial
  }
  for (let i = 255; i < 512; i += 1) EXP[i] = EXP[i - 255]
}

function gfMul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0
  return EXP[LOG[a] + LOG[b]]
}

/** The generator polynomial for `degree` error-correction codewords. */
function generatorPoly(degree: number): Uint8Array {
  let poly = new Uint8Array([1])
  for (let i = 0; i < degree; i += 1) {
    const next = new Uint8Array(poly.length + 1)
    for (let j = 0; j < poly.length; j += 1) {
      next[j] ^= poly[j]
      next[j + 1] ^= gfMul(poly[j], EXP[i])
    }
    poly = next
  }
  return poly
}

/** Reed-Solomon remainder of `data` for `degree` check codewords. */
export function eccCodewords(data: Uint8Array, degree: number): Uint8Array {
  const gen = generatorPoly(degree)
  const rest = new Uint8Array(degree)
  for (const byte of data) {
    const factor = byte ^ rest[0]
    rest.copyWithin(0, 1)
    rest[degree - 1] = 0
    if (factor !== 0) {
      for (let i = 0; i < degree; i += 1) rest[i] ^= gfMul(gen[i + 1], factor)
    }
  }
  return rest
}

// -- Bit stream ------------------------------------------------------------

class BitBuffer {
  bits: number[] = []

  put(value: number, length: number): void {
    for (let i = length - 1; i >= 0; i -= 1) this.bits.push((value >>> i) & 1)
  }

  get length(): number {
    return this.bits.length
  }
}

// -- Format and version information ---------------------------------------

/**
 * The 15-bit format string: two bits of EC level, three of mask, a BCH(15,5)
 * check and the fixed 0x5412 mask that keeps it from ever being all zeroes.
 */
export function formatBits(mask: number): number {
  const ecLevelM = 0b00 // level M's own encoding, which is not its rank
  let data = (ecLevelM << 3) | mask
  let rest = data << 10
  for (let i = 4; i >= 0; i -= 1) {
    if (rest & (1 << (i + 10))) rest ^= 0b10100110111 << i
  }
  data = ((data << 10) | rest) ^ 0b101010000010010
  return data
}

/** The 18-bit version string, on versions 7 and up. */
function versionBits(version: number): number {
  let rest = version << 12
  for (let i = 5; i >= 0; i -= 1) {
    if (rest & (1 << (i + 12))) rest ^= 0b1111100100 << i
  }
  return (version << 12) | rest
}

// -- The matrix ------------------------------------------------------------

export interface QrMatrix {
  version: number
  size: number
  /** Row-major; true is a dark module. */
  modules: boolean[][]
  mask: number
}

function placeFunctionPatterns(
  modules: boolean[][],
  reserved: boolean[][],
  version: number,
): void {
  const size = modules.length

  const set = (row: number, col: number, dark: boolean) => {
    modules[row][col] = dark
    reserved[row][col] = true
  }

  // Finder patterns plus their separators.
  for (const [top, left] of [
    [0, 0],
    [0, size - 7],
    [size - 7, 0],
  ]) {
    for (let r = -1; r <= 7; r += 1) {
      for (let c = -1; c <= 7; c += 1) {
        const row = top + r
        const col = left + c
        if (row < 0 || row >= size || col < 0 || col >= size) continue
        const inRing = r >= 0 && r <= 6 && c >= 0 && c <= 6
        const dark =
          inRing &&
          ((r === 0 || r === 6 || c === 0 || c === 6) ||
            (r >= 2 && r <= 4 && c >= 2 && c <= 4))
        set(row, col, dark)
      }
    }
  }

  // Timing patterns.
  for (let i = 8; i < size - 8; i += 1) {
    set(6, i, i % 2 === 0)
    set(i, 6, i % 2 === 0)
  }

  // Alignment patterns, except where they would sit on a finder pattern.
  const centres = ALIGNMENT[version]
  for (const row of centres) {
    for (const col of centres) {
      const onFinder =
        (row <= 8 && col <= 8) || (row <= 8 && col >= size - 9) || (row >= size - 9 && col <= 8)
      if (onFinder) continue
      for (let r = -2; r <= 2; r += 1) {
        for (let c = -2; c <= 2; c += 1) {
          const ring = Math.max(Math.abs(r), Math.abs(c))
          set(row + r, col + c, ring !== 1)
        }
      }
    }
  }

  // The dark module, and the reserved format areas around the finders.
  set(size - 8, 8, true)
  for (let i = 0; i < 9; i += 1) {
    if (!reserved[8][i]) set(8, i, false)
    if (!reserved[i][8]) set(i, 8, false)
  }
  for (let i = 0; i < 8; i += 1) {
    if (!reserved[8][size - 1 - i]) set(8, size - 1 - i, false)
    if (!reserved[size - 1 - i][8]) set(size - 1 - i, 8, false)
  }

  if (version >= 7) {
    const bits = versionBits(version)
    for (let i = 0; i < 18; i += 1) {
      const dark = ((bits >> i) & 1) === 1
      const row = Math.floor(i / 3)
      const col = i % 3
      set(size - 11 + col, row, dark)
      set(row, size - 11 + col, dark)
    }
  }
}

function placeData(modules: boolean[][], reserved: boolean[][], bits: number[]): void {
  const size = modules.length
  let index = 0
  let upward = true
  for (let right = size - 1; right >= 1; right -= 2) {
    // Column 6 is the vertical timing pattern; the zigzag skips over it.
    const rightCol = right <= 6 ? right - 1 : right
    for (let step = 0; step < size; step += 1) {
      const row = upward ? size - 1 - step : step
      for (const col of [rightCol, rightCol - 1]) {
        if (reserved[row][col]) continue
        modules[row][col] = index < bits.length && bits[index] === 1
        index += 1
      }
    }
    upward = !upward
  }
}

function maskAt(mask: number, row: number, col: number): boolean {
  switch (mask) {
    case 0:
      return (row + col) % 2 === 0
    case 1:
      return row % 2 === 0
    case 2:
      return col % 3 === 0
    case 3:
      return (row + col) % 3 === 0
    case 4:
      return (Math.floor(row / 2) + Math.floor(col / 3)) % 2 === 0
    case 5:
      return ((row * col) % 2) + ((row * col) % 3) === 0
    case 6:
      return (((row * col) % 2) + ((row * col) % 3)) % 2 === 0
    default:
      return (((row + col) % 2) + ((row * col) % 3)) % 2 === 0
  }
}

/** The four penalty rules of the specification, summed. The lowest-scoring
 *  mask wins; any mask decodes, so this only decides how pleasant the code is
 *  for a camera. */
export function penalty(modules: boolean[][]): number {
  const size = modules.length
  let score = 0

  const runScore = (line: boolean[]) => {
    let run = 1
    let total = 0
    for (let i = 1; i < size; i += 1) {
      if (line[i] === line[i - 1]) {
        run += 1
      } else {
        if (run >= 5) total += run - 2
        run = 1
      }
    }
    if (run >= 5) total += run - 2
    return total
  }

  for (let i = 0; i < size; i += 1) {
    score += runScore(modules[i])
    score += runScore(modules.map((row) => row[i]))
  }

  for (let row = 0; row < size - 1; row += 1) {
    for (let col = 0; col < size - 1; col += 1) {
      const a = modules[row][col]
      if (
        a === modules[row][col + 1] &&
        a === modules[row + 1][col] &&
        a === modules[row + 1][col + 1]
      ) {
        score += 3
      }
    }
  }

  // The 1:1:3:1:1 finder-like pattern, with four light modules on either side.
  const finder = [true, false, true, true, true, false, true]
  const hasFinder = (line: boolean[], at: number) => {
    for (let i = 0; i < 7; i += 1) if (line[at + i] !== finder[i]) return false
    const before = line.slice(Math.max(0, at - 4), at)
    const after = line.slice(at + 7, at + 11)
    return (
      (before.length === 4 && before.every((m) => !m)) ||
      (after.length === 4 && after.every((m) => !m))
    )
  }
  for (let i = 0; i < size; i += 1) {
    const row = modules[i]
    const col = modules.map((r) => r[i])
    for (let at = 0; at + 7 <= size; at += 1) {
      if (hasFinder(row, at)) score += 40
      if (hasFinder(col, at)) score += 40
    }
  }

  let dark = 0
  for (const row of modules) for (const module of row) if (module) dark += 1
  const percent = (dark * 100) / (size * size)
  score += Math.floor(Math.abs(percent - 50) / 5) * 10

  return score
}

/** How many bytes a version holds in byte mode at level M. */
export function byteCapacity(version: number): number {
  const headerBits = 4 + (version >= 10 ? 16 : 8)
  return dataCodewords(version) - Math.ceil(headerBits / 8)
}

export class QrTooLongError extends Error {
  constructor() {
    super('Der Text ist für einen QR-Code dieser Größe zu lang.')
    this.name = 'QrTooLongError'
  }
}

/**
 * Encodes `text` as a level-M QR code.
 *
 * Throws `QrTooLongError` above 213 bytes — an upload URL is about 100, so
 * the caller is expected to render the link as text either way and treat a
 * missing QR as a missing convenience, not a failure.
 */
export function encodeQr(text: string): QrMatrix {
  const bytes = new TextEncoder().encode(text)

  let version = 0
  for (let candidate = 1; candidate <= 10; candidate += 1) {
    if (bytes.length <= byteCapacity(candidate)) {
      version = candidate
      break
    }
  }
  if (version === 0) throw new QrTooLongError()

  const [ecPerBlock, blocksA, dataA, blocksB, dataB] = BLOCKS_M[version]
  const total = dataCodewords(version)

  const buffer = new BitBuffer()
  buffer.put(0b0100, 4) // byte mode
  buffer.put(bytes.length, version >= 10 ? 16 : 8)
  for (const byte of bytes) buffer.put(byte, 8)
  // Terminator, then pad to a whole codeword, then the specified pad bytes.
  const capacityBits = total * 8
  buffer.put(0, Math.min(4, capacityBits - buffer.length))
  while (buffer.length % 8 !== 0) buffer.put(0, 1)
  const codewords = new Uint8Array(total)
  for (let i = 0; i < buffer.length / 8; i += 1) {
    let byte = 0
    for (let b = 0; b < 8; b += 1) byte = (byte << 1) | buffer.bits[i * 8 + b]
    codewords[i] = byte
  }
  for (let i = buffer.length / 8; i < total; i += 1) {
    codewords[i] = (i - buffer.length / 8) % 2 === 0 ? 0xec : 0x11
  }

  // Split into blocks, compute the check codewords, then interleave both.
  const blocks: { data: Uint8Array; ecc: Uint8Array }[] = []
  let offset = 0
  for (let i = 0; i < blocksA + blocksB; i += 1) {
    const size = i < blocksA ? dataA : dataB
    const data = codewords.slice(offset, offset + size)
    offset += size
    blocks.push({ data, ecc: eccCodewords(data, ecPerBlock) })
  }

  const interleaved: number[] = []
  const maxData = Math.max(dataA, dataB)
  for (let i = 0; i < maxData; i += 1) {
    for (const block of blocks) if (i < block.data.length) interleaved.push(block.data[i])
  }
  for (let i = 0; i < ecPerBlock; i += 1) {
    for (const block of blocks) interleaved.push(block.ecc[i])
  }

  const bits: number[] = []
  for (const byte of interleaved) {
    for (let i = 7; i >= 0; i -= 1) bits.push((byte >> i) & 1)
  }
  for (let i = 0; i < remainderBits(version); i += 1) bits.push(0)

  const size = version * 4 + 17
  const base: boolean[][] = Array.from({ length: size }, () => new Array<boolean>(size).fill(false))
  const reserved: boolean[][] = Array.from({ length: size }, () =>
    new Array<boolean>(size).fill(false),
  )
  placeFunctionPatterns(base, reserved, version)
  placeData(base, reserved, bits)

  let best: QrMatrix | null = null
  let bestScore = Infinity
  for (let mask = 0; mask < 8; mask += 1) {
    const modules = base.map((row) => row.slice())
    for (let row = 0; row < size; row += 1) {
      for (let col = 0; col < size; col += 1) {
        if (!reserved[row][col] && maskAt(mask, row, col)) modules[row][col] = !modules[row][col]
      }
    }
    writeFormat(modules, mask)
    const score = penalty(modules)
    if (score < bestScore) {
      bestScore = score
      best = { version, size, modules, mask }
    }
  }
  return best as QrMatrix
}

function writeFormat(modules: boolean[][], mask: number): void {
  const size = modules.length
  const bits = formatBits(mask)
  const bit = (i: number) => ((bits >> i) & 1) === 1

  // Two copies of the same 15 bits, so a damaged corner still tells the
  // decoder which mask was used. The layout is the specification's; both
  // copies step around the timing row and column.
  for (let i = 0; i <= 5; i += 1) modules[i][8] = bit(i)
  modules[7][8] = bit(6)
  modules[8][8] = bit(7)
  modules[8][7] = bit(8)
  for (let i = 9; i < 15; i += 1) modules[8][14 - i] = bit(i)

  for (let i = 0; i < 8; i += 1) modules[8][size - 1 - i] = bit(i)
  for (let i = 8; i < 15; i += 1) modules[size - 15 + i][8] = bit(i)

  modules[size - 8][8] = true // the dark module, never masked
}
